import { useLayoutEffect, useMemo, useRef } from 'react';
import { typography, radius } from '@/shared/theme/tokens';
import { useFurnaceSvg } from '../../hooks/useFurnaceSvg';
import { HUD, statusOf } from './hudTheme';

const MONO = typography.fontFamily.mono;
const STATUS_RANK = { critical: 0, warning: 1, ok: 2 };

// Worst-status-first, one chip per parameter, live only (stale never floats as a
// zone reading). Capped so the schematic stays legible.
function pickZones(alerts, max = 4) {
  const byName = new Map();
  for (const a of alerts) {
    if (a.isStale) continue;
    const cur = byName.get(a.parameterName);
    if (!cur || STATUS_RANK[a.status] < STATUS_RANK[cur.status]) byName.set(a.parameterName, a);
  }
  return [...byName.values()]
    .sort((x, y) => STATUS_RANK[x.status] - STATUS_RANK[y.status])
    .slice(0, max);
}

// Anchor slots around the furnace — alternating flanks, top-to-bottom.
const SLOTS = [
  { side: 'left', top: '14%' },
  { side: 'right', top: '26%' },
  { side: 'left', top: '56%' },
  { side: 'right', top: '66%' },
];

/**
 * Furnace stage — the spatial centerpiece. The plant SVG renders as a glowing
 * cyan schematic; live alerts anchor to it as zone callouts (worst-status
 * first). An anchor, not the hero: the semaphore + action stack still command
 * attention. Clicking a zone chip selects that alert.
 */
export function FurnaceStage({ svgUrl, alerts = [], flashId, animate, onSelectAlert }) {
  const hostRef = useRef(null);
  const { data: markup, isLoading, isError } = useFurnaceSvg(svgUrl);
  const zones = useMemo(() => pickZones(alerts), [alerts]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return;
    host.innerHTML = markup;
    const svg = host.querySelector('svg');
    if (svg) {
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.display = 'block';
      svg.style.maxHeight = '100%';
    }
  }, [markup]);

  return (
    <div style={{
      position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden',
      borderRadius: radius.lg, border: `1px solid ${HUD.stroke}`,
      background: `radial-gradient(circle at 50% 42%, ${HUD.panelRaised}, ${HUD.void} 72%)`,
    }}>
      {/* faint instrument grid */}
      <span aria-hidden style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(${HUD.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${HUD.gridLine} 1px, transparent 1px)`,
        backgroundSize: '36px 36px', maskImage: `radial-gradient(circle at 50% 45%, #000 30%, transparent 78%)`,
      }}
      />

      {/* corner label */}
      <div style={{
        position: 'absolute', top: 10, left: 12, zIndex: 3,
        display: 'inline-flex', alignItems: 'center', gap: 7,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: radius.full, background: HUD.accent,
          boxShadow: `0 0 8px ${HUD.accent}`,
          animation: animate ? 'hud-pulse 2s steps(1, end) infinite' : 'none',
        }}
        />
        <span style={{
          fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
          letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color: HUD.inkFaint,
        }}>
          Plant Schematic
        </span>
      </div>

      {/* SVG schematic */}
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '38px 24px 24px',
      }}>
        {isLoading && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: HUD.inkFaint }}>ACQUIRING SCHEMATIC…</span>}
        {isError && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 30, color: HUD.inkFaint }}>satellite_alt</span>
            <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: HUD.inkFaint }}>SCHEMATIC UNAVAILABLE</span>
          </div>
        )}
        {!isLoading && !isError && (
          <div
            ref={hostRef}
            style={{
              width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: 0.9,
              // Plant SVG → glowing cyan command schematic.
              filter: 'brightness(0) invert(0.62) sepia(0.6) saturate(3) hue-rotate(150deg) brightness(1.12) drop-shadow(0 0 7px rgba(62,181,231,0.45))',
            }}
          />
        )}
      </div>

      {/* zone callouts */}
      {zones.map((a, i) => {
        const slot = SLOTS[i] ?? SLOTS[SLOTS.length - 1];
        return (
          <ZoneChip
            key={a.id}
            alert={a}
            slot={slot}
            flash={flashId === a.id}
            animate={animate}
            onSelect={onSelectAlert}
          />
        );
      })}

      {/* all-clear pill */}
      {!isLoading && !isError && zones.length === 0 && (
        <div style={{
          position: 'absolute', top: 12, right: 12, zIndex: 3,
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 10px',
          background: statusOf('ok', false).tint, border: `1px solid ${statusOf('ok', false).edge}`,
          borderRadius: radius.full,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: radius.full, background: statusOf('ok', false).color, boxShadow: `0 0 6px ${statusOf('ok', false).glow}` }} />
          <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide, color: HUD.ink }}>ALL ZONES NOMINAL</span>
        </div>
      )}
    </div>
  );
}

function ZoneChip({ alert, slot, flash, animate, onSelect }) {
  const s = statusOf(alert.status, false);
  const clickable = typeof onSelect === 'function';
  const sideStyle = slot.side === 'left' ? { left: 12 } : { right: 12 };

  return (
    <div
      onClick={clickable ? () => onSelect(alert) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(alert); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${alert.parameterName} alert` : undefined}
      style={{
        position: 'absolute', top: slot.top, ...sideStyle, zIndex: 4,
        display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', width: 168,
        background: HUD.panelRaised, border: `1px solid ${s.edge}`, borderRadius: radius.md,
        boxShadow: flash ? `0 0 0 1px ${s.edge}, 0 0 16px -2px ${s.glow}` : `0 6px 18px -12px ${s.glow}`,
        backdropFilter: 'blur(6px)', cursor: clickable ? 'pointer' : 'default',
        animation: flash && animate ? 'hud-flash 0.9s ease-out' : 'none',
      }}
    >
      <span style={{
        width: 9, height: 9, flexShrink: 0, borderRadius: radius.full, background: s.color,
        boxShadow: `0 0 8px ${s.glow}`,
        animation: alert.status === 'critical' && animate ? 'hud-pulse 1.3s steps(1, end) infinite' : 'none',
      }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: MONO, fontSize: typography.fontSize.xs, color: HUD.inkDim,
          letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {alert.parameterName}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
          <span style={{
            fontFamily: MONO, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold,
            color: s.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
          }}>
            {alert.liveValue}
          </span>
          {alert.unit && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: HUD.inkFaint }}>{alert.unit}</span>}
        </div>
      </div>
    </div>
  );
}
